/** @format */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { Select, Tabs } from 'antd';
import Filter from 'img/ArrowsDownUp.svg';
import OfficeNames from 'components/officeNames';
import PoliciesTableContainer from 'features/policies/containers/PoliciesTabsContainer';
import ACARequestsTableContainer from 'features/policies/containers/ACARequestsTableContainer';
import CustomerProfile from 'features/customer-profile/containers/CustomerProfile';
import { SaleSubmissionFormContainer } from 'features/saleSubmission';
import { Layout } from 'features/layout';

interface FormPageProps {
  type?: 'policies';
}

const FormPage: React.FC<FormPageProps> = () => {
  const [filterBy, setFilterBy] = useState<'office' | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'paid' | 'postdate' | 'aca-request'>('paid');
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  
  // Get user data from Redux
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const userRole = userData?.role;

  // Get selected form ID from Redux
  const selectedFormId = useSelector((state: RootState) => (state.button as any).selectedFormId);

  // Get selected office from Redux
  const selectedOfficeId = useSelector(
    (state: RootState) => (state.dropDown as any).officeId
  );

  // Get start date and end date from Redux
  const startDate = useSelector(
    (state: RootState) => (state.dropDown as any).startDate
  );
  const endDate = useSelector(
    (state: RootState) => (state.dropDown as any).endDate
  );

  // If user is not root_user or root_custo and doesn't have downlineAccess, force their office
  const isRootUser = userRole && ['root_user', 'root_custo'].includes(userRole);
  const hasDownlineAccess = userData?.userAccess.downlineAccess === true;
  const canSelectOffice = isRootUser || hasDownlineAccess;
  const canViewAllOffices = isRootUser; // Only root users can see "All Offices"
  
  // Use user's office if they can't select offices
  const effectiveOfficeId = canSelectOffice ? selectedOfficeId : userData?.officeId;
  // Non-root users with downline access default to 'office' filter
  const effectiveFilterBy = !canSelectOffice ? 'office' : (hasDownlineAccess && !isRootUser ? 'office' : filterBy);

  const filter = {
    ...(effectiveFilterBy === 'office' && effectiveOfficeId ? { officeId: effectiveOfficeId } : {}),
    startDate,
    endDate,
  };

  const handleFilterChange = (value: string) => {
    setFilterBy(value as 'office' | 'all');
  };

  const hideSubmissionForm = () => {
    setShowSubmissionForm(false);
  };

  // If showing submission form
  if (showSubmissionForm) {
    return (
      <Layout>
        <div className='form-page-container' style={{ position: 'relative', width: '100%', padding: '20px' }}>
          <SaleSubmissionFormContainer hideForm={hideSubmissionForm} showUserSelection={true} />
        </div>
      </Layout>
    );
  }

  // If a form is selected, show the customer profile
  if (selectedFormId) {
    return (
      <Layout>

        <CustomerProfile />
       
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='form-page-container' style={{ position: 'relative', width: '100%' }}>
       
        <div className='form-page-header' style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          {userRole === 'admin' && (
            <button
              onClick={() => setShowSubmissionForm(true)}
              className='enable-form-button'
            >
              Submit a form
            </button>
          )}
        </div>

        <div className='filter-container' style={{ marginBottom: '20px' }}>
          <div className='filter'>
            <img src={Filter} alt='Filter' />
            <span>Filter By </span>
            <div className='a-p-tab-wrapper'>
              {canViewAllOffices && (
                <Select
                  value={filterBy}
                  onChange={handleFilterChange}
                  style={{ width: 150 }}
                >
                  <Select.Option value='all'>All Offices</Select.Option>
                  <Select.Option value='office'>Office</Select.Option>
                </Select>
              )}
              {canSelectOffice && <OfficeNames />}
            </div>
          </div>
        </div>

        {/* Ant Design Tabs for Paid vs Post-Dated vs ACA Request */}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'paid' | 'postdate' | 'aca-request')}
          style={{ width: '100%' }}
          items={[
            {
              key: 'paid',
              label: 'Paid Policies',
              children: <div style={{ width: '100%' }}><PoliciesTableContainer filter={{ ...filter, status: 'Paid' }} /></div>,
            },
            {
              key: 'postdate',
              label: 'Post Dates',
              children: <div style={{ width: '100%' }}><PoliciesTableContainer filter={{ ...filter, isPostDate: true }} /></div>,
            },
            {
              key: 'aca-request',
              label: 'ACA Request Forms',
              children: <div style={{ width: '100%' }}><ACARequestsTableContainer filter={filter} /></div>,
            },
          ]}
        />
      </div>
    </Layout>
  );
};

export default FormPage;
